import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getSubCategories, getCategories, createSubCategory, updateSubCategory, deleteSubCategory } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function SubCategoriesPage() {
  const { t, language } = useLanguage();
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [deletingSubcategory, setDeletingSubcategory] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    category_id: '',
    code: '',
    icon: '',
    display_order: 0,
    is_active: true,
  });

  const fetchData = async () => {
    try {
      const [subcatsRes, catsRes] = await Promise.all([
        getSubCategories(),
        getCategories()
      ]);
      setSubcategories(subcatsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return language === 'ar' ? category?.name_ar : (category?.name_en || category?.name_ar);
  };

  const handleOpenDialog = (subcategory = null) => {
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setFormData({
        name_ar: subcategory.name_ar || '',
        name_en: subcategory.name_en || '',
        category_id: subcategory.category_id || '',
        code: subcategory.code || '',
        icon: subcategory.icon || '',
        display_order: subcategory.display_order || 0,
        is_active: subcategory.is_active !== false,
      });
    } else {
      setEditingSubcategory(null);
      setFormData({ name_ar: '', name_en: '', category_id: '', code: '', icon: '', display_order: 0, is_active: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name_ar.trim() || !formData.category_id) {
      toast.error(t('validation.required'));
      return;
    }
    
    setSaving(true);
    try {
      if (editingSubcategory) {
        await updateSubCategory(editingSubcategory.id, formData);
        toast.success(t('success.updated'));
      } else {
        await createSubCategory(formData);
        toast.success(t('success.created'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingSubcategory) return;
    
    try {
      await deleteSubCategory(deletingSubcategory.id);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeletingSubcategory(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    }
  };

  const filteredSubcategories = subcategories.filter(subcategory => {
    const matchesSearch = subcategory.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subcategory.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subcategory.code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || subcategory.category_id === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="subcategories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-10"
              data-testid="subcategories-search"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48" data-testid="subcategories-filter-category">
              <SelectValue placeholder={t('select_category')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {language === 'ar' ? category.name_ar : (category.name_en || category.name_ar)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#0A4C3A] hover:bg-[#073B2D]"
          data-testid="add-subcategory-button"
        >
          <Plus className="w-4 h-4 me-2" />
          {t('add_subcategory')}
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">{t('category_code')}</TableHead>
                <TableHead className="font-medium">{t('subcategory_name_ar')}</TableHead>
                <TableHead className="font-medium">{t('subcategory_name_en')}</TableHead>
                <TableHead className="font-medium">{t('category')}</TableHead>
                <TableHead className="font-medium">{t('status')}</TableHead>
                <TableHead className="font-medium text-center">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubcategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    {t('no_data')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubcategories.map((subcategory) => (
                  <TableRow key={subcategory.id} data-testid={`subcategory-row-${subcategory.id}`}>
                    <TableCell className="font-mono text-sm">{subcategory.code || '-'}</TableCell>
                    <TableCell className="font-medium">{subcategory.name_ar}</TableCell>
                    <TableCell>{subcategory.name_en || '-'}</TableCell>
                    <TableCell>{getCategoryName(subcategory.category_id)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        subcategory.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {subcategory.is_active !== false ? t('active') : t('inactive')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(subcategory)}
                          data-testid={`edit-subcategory-${subcategory.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeletingSubcategory(subcategory);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-subcategory-${subcategory.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSubcategory ? t('edit_subcategory') : t('add_subcategory')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="category_id">{t('category')} *</Label>
              <Select 
                value={formData.category_id} 
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger data-testid="subcategory-category-select">
                  <SelectValue placeholder={t('select_category')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {language === 'ar' ? category.name_ar : (category.name_en || category.name_ar)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_ar">{t('subcategory_name_ar')} *</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                data-testid="subcategory-name-ar"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_en">{t('subcategory_name_en')}</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                dir="ltr"
                data-testid="subcategory-name-en"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t('category_code')}</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  dir="ltr"
                  data-testid="subcategory-code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="icon">{t('category_icon')}</Label>
                <Input
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  dir="ltr"
                  data-testid="subcategory-icon"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">{t('active')}</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="subcategory-is-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#0A4C3A] hover:bg-[#073B2D]"
              data-testid="save-subcategory-button"
            >
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingSubcategory?.name_ar}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-subcategory"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
